/**
 * Robust API Instance - Singleton with automatic auth integration
 * 
 * PREVENTS REGRESSIONS BY:
 * 1. Automatic initialization with auth context
 * 2. Single instance across entire app
 * 3. Built-in validation and error handling
 * 4. No manual setup required
 */

import { RobustApiClient, AuthTokenProvider } from './robustApiClient';

// Auth provider implementation that integrates with RobustAuthContext
class AuthContextProvider implements AuthTokenProvider {
  private authContext: any = null;

  setAuthContext(context: any) {
    this.authContext = context;
  }

  async getAccessToken(): Promise<string> {
    if (!this.authContext) {
      throw new Error('AUTH_CONTEXT_NOT_SET: Auth context must be set before making API calls');
    }
    return await this.authContext.getAccessToken();
  }

  async refreshTokens(): Promise<void> {
    if (!this.authContext) {
      throw new Error('AUTH_CONTEXT_NOT_SET: Cannot refresh tokens without auth context');
    }
    return await this.authContext.refreshTokens();
  }

  validateAuth(): void {
    if (!this.authContext) {
      throw new Error('AUTH_CONTEXT_NOT_SET: Auth context must be set before validation');
    }
    this.authContext.validateAuth();
  }
}

// Singleton instances
const authProvider = new AuthContextProvider();
let robustApiInstance: RobustApiClient | null = null;

// Initialize the robust API client
const initializeRobustApi = (): RobustApiClient => {
  if (!robustApiInstance) {
    robustApiInstance = new RobustApiClient(authProvider);
    // Only log in development
    if (process.env.NODE_ENV === 'development') {
      console.log('✅ ROBUST API: Singleton instance created');
    }
  }
  return robustApiInstance;
};

// Hook for setting auth context (called by RobustAuthProvider)
export const setAuthContextForApi = (authContext: any): void => {
  authProvider.setAuthContext(authContext);
  // Only log in development
  if (process.env.NODE_ENV === 'development') {
    console.log('✅ ROBUST API: Auth context configured');
  }
};

// Get the robust API instance
export const getRobustApiClient = (): RobustApiClient => {
  return initializeRobustApi();
};

// Default export as function for lazy initialization
export default getRobustApiClient;